import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import { SortType } from "../../../models/SortType";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import FormTextField from "../../components/common/form/FormTextField";
import AppButton from "../../components/common/buttons/AppButton";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import PurchaseOrderInvoicesTable from "../../components/invoices/PurchaseOrderInvoicesTable";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  GetPurchaseOrderInvoicesParams,
  PurchaseOrderInvoiceFilters,
} from "../../../models/Invoice/PurchaseOrderInvoiceFilters";
import { InvoicePaymentStatusFilters } from "../../../models/ProjectManagement/ProjectInventoryProduct";

const approvalFilters: FilterValue[] = [
  { id: "approved", value: "Approved" },
  { id: "pending", value: "Pending" },
  { id: "rejected", value: "Rejected" },
];

const PurchaseOrderInvoicesListing: React.FC = () => {
  const theme = useTheme();
  const { userStore, invoiceStore } = useStores();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const parseUrlParams = (): PurchaseOrderInvoiceFilters => {
    return {
      page_no: parseInt(searchParams.get("page_no") || "1", 10),
      limit: parseInt(searchParams.get("limit") || "30", 10),
      sortBy: searchParams.get("sortBy") || "created_date",
      sortType:
        searchParams.get("sortType") === "ASC" ? SortType.ASC : SortType.DESC,
      search_text: searchParams.get("search_text") || "",
      approval_status: searchParams.get("approval_status") || undefined,
      invoice_payment_status:
        searchParams.get("invoice_payment_status") || undefined,
    };
  };

  const initialFilters: PurchaseOrderInvoiceFilters = parseUrlParams();

  const [filters, setFilters] =
    useState<PurchaseOrderInvoiceFilters>(initialFilters);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const [selectedApproval, setSelectedApproval] = useState<FilterValue[]>(
    initialFilters.approval_status
      ? approvalFilters.filter(
          (item) => item.id === initialFilters.approval_status,
        )
      : [],
  );
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<
    FilterValue[]
  >(
    initialFilters.invoice_payment_status
      ? InvoicePaymentStatusFilters.filter(
          (item) => item.id === initialFilters.invoice_payment_status,
        )
      : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_PURCHASE_ORDER_INVOICES])) {
      setIsLoading(true);
      invoiceStore
        .getPurchaseOrderInvoices(filters as GetPurchaseOrderInvoicesParams)
        .catch((err: any) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    // Skip URL update on initial mount to preserve URL params
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value?.toString() || "";
          if (paramValue) {
            searchParams.set(key, paramValue);
          }
        }
      });

      const queryString = searchParams.toString();
      navigate({ search: queryString }, { replace: true });
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, navigate]);

  const handleSearchBar = async (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      setSearchTerm(searchText);
    } else {
      setSearchTerm("");
      setFilters((prev: PurchaseOrderInvoiceFilters) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    const trimmedSearch = searchTerm.trim();
    setSearchTerm(trimmedSearch);
    setFilters((prev: PurchaseOrderInvoiceFilters) => ({
      ...prev,
      search_text: trimmedSearch,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      const trimmedSearch = searchTerm.trim();
      if (trimmedSearch.length > 0) {
        setSearchTerm(trimmedSearch);
        setFilters((prev: PurchaseOrderInvoiceFilters) => ({
          ...prev,
          search_text: trimmedSearch,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setFilters((prev: PurchaseOrderInvoiceFilters) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedApproval([]);
    setSelectedPaymentStatus([]);
    setFilters({
      page_no: 1,
      limit: 30,
      sortBy: "created_date",
      sortType: SortType.DESC,
    });
  };

  const handleApply = () => {
    const trimmedSearch = searchTerm.trim();
    setSearchTerm(trimmedSearch);
    setFilters((prev: PurchaseOrderInvoiceFilters) => ({
      ...prev,
      search_text: trimmedSearch,
      approval_status: selectedApproval[0]?.id || undefined,
      invoice_payment_status: selectedPaymentStatus[0]?.id || undefined,
      page_no: 1,
    }));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setFilters((prev: PurchaseOrderInvoiceFilters) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev: PurchaseOrderInvoiceFilters) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!userStore.hasPermission([
        EmployeeRoles.VIEW_PURCHASE_ORDER_INVOICES,
      ]) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">
                Vendor Invoices
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View Vendor Invoices
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}
      {userStore.hasPermission([
        EmployeeRoles.VIEW_PURCHASE_ORDER_INVOICES,
      ]) && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">
                Vendor Invoices
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View Vendor Invoices
              </CustomTypography>
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isSearchExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">Search</CustomTypography>
              <IconButton size="small">
                {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
              <Grid2 container spacing={2} columns={12} padding={2}>
                <Grid2
                  size={{ xs: 12, sm: 9, md: 11, lg: 11 }}
                  sx={{ padding: 0 }}
                >
                  <FormTextField
                    marginBottom={0}
                    id="purchase-order-invoice-search"
                    placeholder="Search by Purchase Order ID, Invoice Number"
                    type="search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(_, value) => handleSearchBar(value)}
                    onKeyDown={(e) => handleKeyDown(e)}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 3, md: 1, lg: 1 }}>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearchSubmit}
                    customStyles={{ width: "100%", height: 40 }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <AppSearchDropdownMulti
                    title="Approval"
                    isMultiSelect={false}
                    value={selectedApproval}
                    list={approvalFilters}
                    local={true}
                    handleSelect={(items: FilterValue[]) => {
                      setSelectedApproval(items);
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <AppSearchDropdownMulti
                    title="Payment Status"
                    isMultiSelect={false}
                    value={selectedPaymentStatus}
                    list={InvoicePaymentStatusFilters}
                    local={true}
                    handleSelect={(items: FilterValue[]) => {
                      setSelectedPaymentStatus(items);
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={resetFilters}
                      customStyles={{ width: "100%" }}
                    />
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApply}
                      customStyles={{ width: "100%" }}
                    />
                  </Box>
                </Grid2>
              </Grid2>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {invoiceStore.purchaseOrderInvoices?.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No purchase order invoices match the current search
                    criteria.
                  </CustomTypography>
                </Box>
              )}
              {invoiceStore.purchaseOrderInvoices?.length > 0 && (
                <PurchaseOrderInvoicesTable
                  maxHeight={window.innerHeight - 350}
                  purchaseOrderInvoices={
                    invoiceStore.purchaseOrderInvoices || []
                  }
                  limit={filters.limit || 30}
                  page_no={filters.page_no}
                  total_count={invoiceStore.total_count}
                  total_pages={invoiceStore.total_pages}
                  sortBy={filters.sortBy}
                  sortType={filters.sortType}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={(column: string, sortType: SortType) => {
                    setFilters((prev: PurchaseOrderInvoiceFilters) => ({
                      ...prev,
                      sortBy: column,
                      sortType: sortType,
                    }));
                  }}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(PurchaseOrderInvoicesListing);
